import { useState, useCallback, useMemo, useEffect } from 'react';
import { TreeNode, Bookmark, DocumentPreview, ViewMode } from '../types';
import { TreeItem } from './TreeItem';
import { BookmarkIcon, Search, Filter, Plus, Minus, Layers, RefreshCcw } from 'lucide-react';
import './TreePanel.css';

interface TreePanelProps {
  tree: TreeNode[];
  onDocumentSelect: (document: DocumentPreview) => void;
  onBookmarkToggle: (node: TreeNode, path: string[]) => void;
  bookmarks: Bookmark[];
  isBookmarked: (node: TreeNode) => boolean;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  getOpenCommentCount?: (node: TreeNode) => number;
}

export function TreePanel({
  tree,
  onDocumentSelect,
  onBookmarkToggle,
  bookmarks,
  isBookmarked,
  viewMode,
  onViewModeChange,
  getOpenCommentCount,
}: TreePanelProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState<'all' | 'm1' | 'm2' | 'm3' | 'm4' | 'm5'>('all');

  useEffect(() => {
     if (!tree || tree.length === 0) {
      setModuleFilter(prev => (prev === 'all' ? prev : 'all'));
      setSearchQuery(prev => (prev ? '' : prev));
      setShowBookmarks(prev => (prev ? false : prev));
      setExpandedNodes(prev => (prev.size === 0 ? prev : new Set()));
    }
  }, [tree]);

  const moduleFilteredTree = useMemo(() => {
    return filterTreeByModule(tree, moduleFilter);
  }, [tree, moduleFilter]);

  // Filter tree based on search query
  const filteredTree = useMemo(() => {
    if (!searchQuery.trim()) return moduleFilteredTree;
    return filterTree(moduleFilteredTree, searchQuery.toLowerCase());
  }, [moduleFilteredTree, searchQuery]);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchTree(moduleFilteredTree, searchQuery.toLowerCase());
  }, [moduleFilteredTree, searchQuery]);

  const handleToggleNode = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    const allNodeIds = new Set<string>();
    // Name-based keys; 4-digit sequence folders are transparent so the same
    // folder has the same key in current and cumulative views.
    const collectIds = (nodes: TreeNode[], base: string = '') => {
      nodes.forEach((node) => {
        if (node.type === 'folder') {
          const key = base ? `${base}/${node.name}` : node.name;
          allNodeIds.add(key);
          const childBase = /^\d{4}$/.test(node.name) ? base : key;
          collectIds(node.children || [], childBase);
        }
      });
    };
    collectIds(tree);
    setExpandedNodes(allNodeIds);
  }, [tree]);

  const handleCollapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  const handleViewModeToggle = useCallback(() => {
    const newMode = viewMode.type === 'current' ? 'cumulative' : 'current';
    onViewModeChange({ type: newMode, sequenceId: undefined });
  }, [viewMode.type, onViewModeChange]);

  const handleDocumentClick = useCallback((node: TreeNode) => {
    if (node.type === 'file') {
      const fileWithPath = node.file as File & { webkitRelativePath?: string };
      const derivedPath =
        fileWithPath?.webkitRelativePath ||
        node.originalName ||
        node.name;
      const document: DocumentPreview = {
        title: node.xmlTitle || node.name,
        file: node.file,
        seq: node.sequenceId,
        href: node.originalName || node.name,
        op: node.lifecycleOp,
        path: derivedPath?.toLowerCase(),
      };
      onDocumentSelect(document);
    }
  }, [onDocumentSelect]);

  const bookmarkNodes = useMemo(() => {
    if (!showBookmarks) return [];
    const nodes = bookmarks.map(b => b.node);
    if (moduleFilter === 'all') return nodes;
    return nodes.filter(node => {
      if (node.type === 'file') {
        return node.module?.toLowerCase() === moduleFilter;
      }
      return node.name.toLowerCase() === moduleFilter;
    });
  }, [bookmarks, showBookmarks, moduleFilter]);

  const displayTree = useMemo(() => {
    return showBookmarks
      ? bookmarkNodes
      : searchQuery.trim()
      ? searchResults.map(r => r.node)
      : filteredTree;
  }, [bookmarkNodes, showBookmarks, searchQuery, searchResults, filteredTree]);

  // Build stable node keys from folder NAMES (not positions). 4-digit sequence
  // folders are transparent in child keys, so "m3/32-body-data" is the same key
  // in per-sequence view (where 0000 is the root) and in cumulative view (where
  // modules are the root). This keeps the expansion state intact when switching
  // views, sequences, or filters.
  const buildStableNodePath = useCallback((node: TreeNode): string => {
    const findNodeInTree = (nodes: TreeNode[], targetNode: TreeNode, base: string): string | null => {
      for (const currentNode of nodes) {
        const key = base ? `${base}/${currentNode.name}` : currentNode.name;

        if (currentNode.name === targetNode.name && currentNode.type === targetNode.type) {
          if (currentNode.type === 'file' && targetNode.type === 'file') {
            if (currentNode.originalName === targetNode.originalName ||
                (!currentNode.originalName && !targetNode.originalName)) {
              return key;
            }
          } else {
            return key;
          }
        }

        if (currentNode.type === 'folder' && currentNode.children) {
          const childBase = /^\d{4}$/.test(currentNode.name) ? base : key;
          const found = findNodeInTree(currentNode.children, targetNode, childBase);
          if (found) return found;
        }
      }
      return null;
    };

    return findNodeInTree(tree, node, '') || node.name;
  }, [tree]);

  const handleRefresh = useCallback(() => {
    setModuleFilter('all');
    setSearchQuery('');
    setShowBookmarks(false);
  }, []);

  return (
    <div className="tree-panel">
      <div className="tree-header" aria-label="Outline controls">
        <div className="tree-controls">
          <div className="toolbar-left-controls">
          <button
            className={`control-button ${viewMode.type === 'cumulative' ? 'active' : ''}`}
            onClick={handleViewModeToggle}
            title={`Switch to ${viewMode.type === 'current' ? 'cumulative' : 'current'} view`}
          >
            <Layers className="icon" />
          </button>
          <span className="control-separator">|</span>
          
          <button
            className={`control-button ${showBookmarks ? 'active' : ''}`}
            onClick={() => setShowBookmarks(!showBookmarks)}
            title="Toggle bookmarks"
          >
            <BookmarkIcon className="icon" />
          </button>
          <span className="control-separator">|</span>
          
          <div className="expand-controls">
            <button
              className="control-button"
              onClick={handleExpandAll}
              title="Expand all"
            >
              <Plus className="icon" />
            </button>
            <button
              className="control-button"
              onClick={handleCollapseAll}
              title="Collapse all"
            >
              <Minus className="icon" />
            </button>
          </div>
          </div>

          <div className="tree-filters">
            <select
              className="module-filter"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value as typeof moduleFilter)}
            >
              <option value="all">All Modules</option>
              <option value="m1">Module 1</option>
              <option value="m2">Module 2</option>
              <option value="m3">Module 3</option>
              <option value="m4">Module 4</option>
              <option value="m5">Module 5</option>
            </select>

            <div className="search-container">
              <Search className="search-icon" />
              <input
                type="search"
                className="search-input"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
                name="document-tree-search"
              />
            </div>

            <span className="control-separator">|</span>
            <button
              className="control-button"
              onClick={handleRefresh}
              title="Reset filters"
            >
              <RefreshCcw className="icon" />
            </button>
          </div>
        </div>
      </div>

      <div className="tree-content">
        {displayTree.length === 0 ? (
          <div className="tree-content-empty">
            <div className="empty-state">
              <Filter className="empty-icon" />
              <h3>No documents loaded</h3>
              <p className="empty-hint">Select an eCTD folder to get started or reset filters.</p>
            </div>
          </div>
        ) : (
          <div className="tree-list">
              {displayTree.map((node, index) => {
                const stablePath = buildStableNodePath(node);
                return (
                  <TreeItem
                    key={`${node.type}-${node.name}-${index}`}
                    node={node}
                    level={0}
                    expandedNodes={expandedNodes}
                    onToggle={handleToggleNode}
                    onDocumentClick={handleDocumentClick}
                    onBookmarkToggle={onBookmarkToggle}
                    isBookmarked={isBookmarked}
                    searchQuery={searchQuery}
                    nodePath={stablePath}
                    originalTree={tree}
                    getOpenCommentCount={getOpenCommentCount}
                  />
                );
              })}
          </div>
        )}

        {displayTree.length === 0 && (searchQuery || showBookmarks) && (
          <div className="empty-state">
            {searchQuery ? (
              <div>
                <Search className="empty-icon" />
                <h3>No documents found for "{searchQuery}"</h3>
                <p className="empty-hint">Try a different search term or clear the search</p>
              </div>
            ) : showBookmarks ? (
              <div>
                <BookmarkIcon className="empty-icon" />
                <h3>No bookmarks yet</h3>
                <p className="empty-hint">Click the bookmark icon on any document to save it</p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper functions
function filterTreeByModule(nodes: TreeNode[] = [], moduleFilter: 'all' | 'm1' | 'm2' | 'm3' | 'm4' | 'm5'): TreeNode[] {
  if (moduleFilter === 'all') return nodes;

  return (nodes || []).reduce<TreeNode[]>((acc, node) => {
    if (node.type === 'folder') {
      const matchesModule = node.name.toLowerCase() === moduleFilter;
      const filteredChildren = filterTreeByModule(node.children || [], moduleFilter);

      if (matchesModule) {
        acc.push({
          ...node,
          children: node.children ? [...node.children] : [],
        });
      } else if (filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren,
        });
      }
    }

    return acc;
  }, []);
}

function filterTree(nodes: TreeNode[], searchTerm: string): TreeNode[] {
  if (!nodes) return [];
  
  return nodes.filter(node => {
    if (node.type === 'file') {
      const searchableText = [
        node.xmlTitle || '',
        node.originalName || node.name || '',
        node.lifecycleOp || '',
        node.sequenceId || '',
        node.stfStudyId || '',
        node.stfVersion || ''
      ].join(' ').toLowerCase();
      
      return searchableText.includes(searchTerm);
    } else {
      let folderSearchable = [node.name];
      if (node.metadata) {
        Object.entries(node.metadata).forEach(([key, value]) => {
          folderSearchable.push(`${key}="${value}"`);
        });
      }
      
      const folderSearchText = folderSearchable.join(' ').toLowerCase();
      if (folderSearchText.includes(searchTerm)) {
        return true;
      }
      
      const filteredChildren = filterTree(node.children, searchTerm);
      if (filteredChildren.length > 0) {
        return true;
      }
      return false;
    }
  });
}

function searchTree(nodes: TreeNode[], searchTerm: string): Array<{ node: TreeNode; path: string[]; score: number }> {
  const results: Array<{ node: TreeNode; path: string[]; score: number }> = [];
  
  const search = (nodeList: TreeNode[], path: string[] = []) => {
    nodeList.forEach(node => {
      const currentPath = [...path, node.name];
      
      if (node.type === 'file') {
        const searchableText = [
          node.xmlTitle || '',
          node.originalName || node.name || '',
          node.lifecycleOp || '',
          node.sequenceId || '',
          node.stfStudyId || '',
          node.stfVersion || ''
        ].join(' ').toLowerCase();
        
        if (searchableText.includes(searchTerm)) {
          const score = calculateScore(searchableText, searchTerm);
          results.push({ node, path: currentPath, score });
        }
      } else {
        let folderSearchable = [node.name];
        if (node.metadata) {
          Object.entries(node.metadata).forEach(([key, value]) => {
            folderSearchable.push(`${key}="${value}"`);
          });
        }
        
        const folderSearchText = folderSearchable.join(' ').toLowerCase();
        if (folderSearchText.includes(searchTerm)) {
          const score = calculateScore(folderSearchText, searchTerm);
          results.push({ node, path: currentPath, score });
        }
        
        search(node.children, currentPath);
      }
    });
  };
  
  search(nodes);
  return results.sort((a, b) => b.score - a.score);
}

function calculateScore(text: string, searchTerm: string): number {
  let score = 0;
  const lowerText = text.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase();
  
  // Exact match gets highest score
  if (lowerText.includes(lowerSearch)) {
    score += 100;
  }
  
  // Word boundary matches get higher score
  const words = lowerSearch.split(/\s+/);
  words.forEach(word => {
    if (lowerText.includes(word)) {
      score += 10;
    }
  });
  
  // Title matches get bonus
  if (lowerText.startsWith(lowerSearch)) {
    score += 50;
  }
  
  return score;
}

